import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCall } from '../contexts/CallContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { setNativeVideoLayout, usesNativeCallMedia } from '../utils/nativePeer';

const formatDuration = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

// compact: five controls have to fit across a phone during video calls
const RoundButton = ({ onClick, label, icon, className, active = false, compact = false, disabled = false }) => (
  <div className="flex flex-col items-center gap-2">
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      className={`${compact ? 'w-14 h-14 text-xl' : 'w-[68px] h-[68px] text-2xl'} rounded-full flex items-center justify-center active:scale-90 transition-all disabled:opacity-50 ${className}`}
    >
      <i className={icon} />
    </button>
    <span className={`${compact ? 'text-[11px] max-w-[64px]' : 'text-[13px] max-w-[88px]'} text-white/80 truncate`}>{label}</span>
  </div>
);

// Plays a MediaStream in a muted <video> (call audio goes through <audio>)
const VideoView = ({ stream, mirrored, className }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream || null;
    if (stream) el.play().catch(() => {});
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      className={className}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
    />
  );
};

const DEVICE_ICONS = {
  earpiece: 'fas fa-mobile-screen',
  speaker: 'fas fa-volume-high',
  wired: 'fas fa-headphones',
  bluetooth: 'fab fa-bluetooth-b',
};

// Where call audio plays. With only phone + speaker it's a Speaker toggle;
// with a headset connected it opens a picker (like the iOS Phone app).
const AudioOutputButton = ({ audioRoute, onSelect, compact = false }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { devices, current } = audioRoute;
  if (!devices.length) return null; // web: no routing control

  const currentDevice = devices.find((d) => d.id === current);
  const speaker = devices.find((d) => d.type === 'speaker');
  const usesPicker = devices.length > 2;

  if (!usesPicker) {
    const onSpeaker = currentDevice?.type === 'speaker';
    const other = devices.find((d) => d.type !== 'speaker');
    return (
      <RoundButton
        onClick={() => {
          const target = onSpeaker ? other?.id : speaker?.id;
          if (target) onSelect(target);
        }}
        label="Speaker"
        compact={compact}
        icon="fas fa-volume-high"
        active={onSpeaker}
        className={onSpeaker ? 'bg-white text-zinc-900' : 'bg-white/15 hover:bg-white/25'}
      />
    );
  }

  const offDevice = !currentDevice || currentDevice.type === 'earpiece';
  return (
    <>
      <RoundButton
        onClick={() => setPickerOpen(true)}
        label={currentDevice?.name || 'Audio'}
        compact={compact}
        icon={DEVICE_ICONS[currentDevice?.type] || 'fas fa-volume-high'}
        active={!offDevice}
        className={offDevice ? 'bg-white/15 hover:bg-white/25' : 'bg-white text-zinc-900'}
      />
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[210] flex items-end bg-black/50"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="w-full bg-zinc-900 rounded-t-3xl p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}
            role="listbox"
            aria-label="Audio output"
          >
            <div className="w-10 h-1.5 bg-white/20 rounded-full mx-auto mb-3" />
            {devices.map((d) => (
              <button
                key={d.id}
                type="button"
                role="option"
                aria-selected={d.id === current}
                onClick={() => {
                  onSelect(d.id);
                  setPickerOpen(false);
                }}
                className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl text-left text-[16px] text-white active:bg-white/10"
              >
                <i className={`${DEVICE_ICONS[d.type]} w-5 text-center text-white/80`} />
                <span className="flex-1 truncate">{d.name}</span>
                {d.id === current && <i className="fas fa-check text-indigo-400" />}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="w-full mt-1.5 py-3.5 rounded-2xl bg-white/10 text-white font-semibold active:bg-white/15"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const Avatar = ({ peer, name, ringing, size = 'w-28 h-28 text-4xl' }) => (
  <div className="relative">
    {ringing && <span className="absolute inset-0 rounded-full bg-indigo-400/30 animate-ping" />}
    {peer?.profilePicture ? (
      <img
        src={getMediaUrl(peer.profilePicture)}
        alt={name}
        className={`relative ${size} rounded-full object-cover ring-4 ring-white/10`}
      />
    ) : (
      <div className={`relative ${size} rounded-full bg-indigo-600 flex items-center justify-center font-bold ring-4 ring-white/10`}>
        {name.charAt(0).toUpperCase()}
      </div>
    )}
  </div>
);

const LIVE_STATUSES = ['outgoing', 'connecting', 'active'];

const CallOverlay = () => {
  const callCtx = useCall();
  const audioRef = useRef(null);
  const previewRef = useRef(null);
  const [now, setNow] = useState(Date.now());
  // False for a moment after answering, so repeated taps on Accept can't hang up
  const [endArmed, setEndArmed] = useState(true);

  const call = callCtx?.call;
  const status = call?.status;
  const remoteStream = callCtx?.remoteStream;
  const remoteVideoTrack = callCtx?.remoteVideoTrack;
  const camera = callCtx?.camera;
  const hasRemoteVideo = !!callCtx?.hasRemoteVideo;
  const nativeVideo = usesNativeCallMedia();

  const remoteVideoStream = useMemo(
    () => (remoteVideoTrack ? new MediaStream([remoteVideoTrack]) : null),
    [remoteVideoTrack]
  );
  const live = LIVE_STATUSES.includes(status);
  const showRemoteVideo = hasRemoteVideo && (status === 'connecting' || status === 'active');
  const showPreview = !!camera?.on && live;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.srcObject !== remoteStream) {
      audio.srcObject = remoteStream || null;
    }

    if (remoteStream) {
      audio.volume = 1;
      audio.muted = false;
      audio.play().catch((err) => {
        console.error('Audio play error:', err);
        // If autoplay blocked, try again after a short delay
        setTimeout(() => {
          audio.play().catch((e) => console.error('Audio replay failed:', e));
        }, 500);
      });
    }
  }, [remoteStream]);

  useEffect(() => {
    if (status !== 'active') return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);

  const answeredAt = call?.answeredAt;
  useEffect(() => {
    if (!answeredAt) return undefined;
    setEndArmed(false);
    const id = setTimeout(() => setEndArmed(true), 2000);
    return () => clearTimeout(id);
  }, [answeredAt]);

  // iOS: native views draw the video; tell them where, in page coordinates.
  // The page goes see-through while the remote video shows behind it.
  useLayoutEffect(() => {
    if (!nativeVideo) return undefined;
    const update = () => {
      setNativeVideoLayout({
        remote: showRemoteVideo ? { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight } : null,
        local: showPreview && previewRef.current ? previewRef.current.getBoundingClientRect() : null,
        mirrorLocal: camera?.facing !== 'environment',
      });
    };
    update();
    document.documentElement.classList.toggle('native-video-call', showRemoteVideo);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [nativeVideo, showRemoteVideo, showPreview, camera?.facing]);

  useEffect(() => () => {
    if (!nativeVideo) return;
    document.documentElement.classList.remove('native-video-call');
    setNativeVideoLayout({});
  }, [nativeVideo]);

  if (!callCtx || !call || status === 'idle') return null;

  const { peer } = call;
  const {
    muted, acceptCall, declineCall, hangUp, toggleMute, audioRoute, selectAudioOutput,
    isVideo, canUseCamera, canFlipCamera, toggleCamera, flipCamera, localVideoStream,
  } = callCtx;
  const name = peer?.username || peer?.handle || 'Unknown';

  const activeDuration = formatDuration(now - (call.startedAt || now));
  const activeLabel = call.reconnecting
    ? 'Reconnecting…'
    : call.networkQuality === 'poor'
    ? `${activeDuration} • Poor connection`
    : activeDuration;

  const statusText = {
    outgoing: call.callId ? 'Ringing…' : 'Calling…',
    incoming: call.video ? 'Incoming video call' : 'Incoming voice call',
    connecting: 'Connecting…',
    active: activeLabel,
    ended: call.endLabel,
  }[status];

  const ringing = status === 'outgoing' || status === 'incoming';
  const compact = isVideo;

  const isReconnecting = status === 'active' && call.reconnecting;

  const statusLine = (
    <p
      className={`mt-1.5 text-[15px] ${
        status === 'ended'
          ? 'text-rose-300'
          : isReconnecting
          ? 'text-amber-300 font-medium flex items-center justify-center gap-1.5 animate-pulse'
          : 'text-white/70'
      } ${status === 'active' && !isReconnecting ? 'tabular-nums' : ''}`}
      aria-live="polite"
    >
      {isReconnecting && <i className="fas fa-arrows-rotate fa-spin text-xs" />}
      {statusText}
    </p>
  );

  const nameLine = (
    <h2 className="text-[26px] font-semibold tracking-[-0.01em] flex items-center gap-2">
      <bdi>{name}</bdi>
      {peer?.isVerified && <i className="fas fa-check-circle text-sky-400 text-base" />}
    </h2>
  );

  let controls;
  if (status === 'incoming') {
    controls = (
      <div className={`w-full ${call.video ? 'max-w-sm' : 'max-w-xs'} flex items-center justify-between`}>
        <RoundButton onClick={declineCall} label="Decline" icon="fas fa-phone-slash" className="bg-rose-500 hover:bg-rose-600" />
        {call.video && (
          <RoundButton
            onClick={() => acceptCall({ withVideo: false })}
            label="Audio only"
            icon="fas fa-phone"
            className="bg-white/15 hover:bg-white/25"
          />
        )}
        <RoundButton
          onClick={() => acceptCall()}
          label="Accept"
          icon={call.video ? 'fas fa-video' : 'fas fa-phone'}
          className="bg-emerald-500 hover:bg-emerald-600 animate-bounce"
        />
      </div>
    );
  } else if (status === 'ended') {
    controls = <div className="h-[212px]" />; // keeps the layout from jumping
  } else {
    controls = (
      <div className="w-full flex flex-col items-center gap-6">
      <div className={`w-full ${compact ? 'max-w-sm' : 'max-w-xs'} flex items-start justify-evenly`}>
        <RoundButton
          compact={compact}
          onClick={toggleMute}
          label={muted ? 'Unmute' : 'Mute'}
          icon={muted ? 'fas fa-microphone-slash' : 'fas fa-microphone'}
          active={muted}
          className={muted ? 'bg-white text-zinc-900' : 'bg-white/15 hover:bg-white/25'}
        />
        {canUseCamera && (
          <RoundButton
            compact={compact}
            onClick={toggleCamera}
            label={camera.on ? 'Camera' : 'Camera off'}
            icon={camera.on ? 'fas fa-video' : 'fas fa-video-slash'}
            active={!camera.on}
            className={camera.on ? 'bg-white/15 hover:bg-white/25' : 'bg-white text-zinc-900'}
          />
        )}
        {canFlipCamera && camera.on && (
          <RoundButton
            compact={compact}
            onClick={flipCamera}
            label="Flip"
            icon="fas fa-camera-rotate"
            className="bg-white/15 hover:bg-white/25"
          />
        )}
        <AudioOutputButton audioRoute={audioRoute} onSelect={selectAudioOutput} compact={compact} />
      </div>

      {/* Hang up gets its own centered row, never where Accept was, and ignores
          taps for a moment after answering: extra taps on a slow-to-respond
          Accept used to land on it and end the call */}
      <RoundButton
        onClick={hangUp}
        disabled={!endArmed}
        label="End"
        icon="fas fa-phone-slash"
        className="bg-rose-500 hover:bg-rose-600"
      />
      </div>
    );
  }

  const overlay = isVideo && status !== 'incoming' ? (
    // ── Video layout ──
    <div
      className={`fixed inset-0 z-[200] text-white animate-in fade-in duration-200 ${
        // iOS: transparent so the native remote video behind the page shows
        nativeVideo && showRemoteVideo ? 'bg-transparent' : 'bg-zinc-950'
      }`}
      role="dialog"
      aria-label={`Video call with ${name}`}
    >
      <audio ref={audioRef} autoPlay muted={false} />{/* WebRTC remote audio */}

      {showRemoteVideo && !nativeVideo && (
        <VideoView stream={remoteVideoStream} className="absolute inset-0 w-full h-full object-cover bg-black" />
      )}

      {/* Remote camera off (or not there yet): their picture instead */}
      {!showRemoteVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-indigo-950 via-zinc-950 to-black">
          <Avatar peer={peer} name={name} ringing={ringing} />
          {status === 'active' && <p className="mt-4 text-[14px] text-white/60">Camera is off</p>}
        </div>
      )}

      {/* Name and timer, top-left (the self-preview owns the top-right) */}
      <div className="absolute inset-x-0 top-0 px-5 pt-[calc(1rem+env(safe-area-inset-top))] pb-10 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
        <div className="pr-32 [&_h2]:text-[22px]">
          {nameLine}
          {statusLine}
        </div>
      </div>

      {/* Self-preview. On iOS this box only reserves the spot for the native view. */}
      {showPreview && (
        <div
          ref={previewRef}
          className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] w-[104px] h-[156px] rounded-2xl overflow-hidden ring-1 ring-white/20 shadow-xl bg-black"
        >
          {!nativeVideo && (
            <VideoView
              stream={localVideoStream}
              mirrored={camera.facing !== 'environment'}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex justify-center px-4 pt-12 pb-[calc(2rem+env(safe-area-inset-bottom))] bg-gradient-to-t from-black/70 to-transparent">
        {controls}
      </div>
    </div>
  ) : (
    // ── Voice layout (also every incoming call) ──
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-between bg-gradient-to-b from-indigo-950 via-zinc-950 to-black text-white pt-[calc(4.5rem+env(safe-area-inset-top))] pb-[calc(3.5rem+env(safe-area-inset-bottom))] px-6 animate-in fade-in duration-200"
      role="dialog"
      aria-label={`${call.video ? 'Video' : 'Voice'} call with ${name}`}
    >
      <audio ref={audioRef} autoPlay muted={false} />{/* WebRTC remote audio */}

      {/* Peer identity */}
      <div className="flex flex-col items-center text-center">
        <Avatar peer={peer} name={name} ringing={ringing} />
        <div className="mt-6">{nameLine}</div>
        {statusLine}
      </div>

      {controls}
    </div>
  );

  // Outside #root, which is hidden while iOS shows native video behind the page
  return createPortal(overlay, document.body);
};

export default CallOverlay;
